/* eslint-disable */
import axios, { AxiosRequestConfig } from "axios";
import { redirectRequestConfig } from "@/guards/auth/auth-config";
import { toast } from "@/components";
import { acquireMsalToken } from "@/guards/auth/acquire-msal-token";
import { appConfig } from "@/guards/auth/azure-b2c.config";
import { getBooleanConfigValue } from "@/utils";
import { logger } from "@/utils/logger";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { getReasonPhrase, StatusCodes } from "http-status-codes";

export const predefineAxiosInterceptors = () => {
  const isAuthDisabled = getBooleanConfigValue(
    appConfig?.FEATURES?.IS_AUTH_DISABLED,
  );
  const { instance } = useMsal();

  let consecutiveErrors = 0;
  let lastErrorTime = 0;

  const errorThreshold = 3;
  const timeThreshold = 5000;

  axios.interceptors.response.use(
    function (response) {
      consecutiveErrors = 0;
      return response;
    },
    function (error) {
      const currentTime = Date.now();
      if (
        !axios.isCancel(error) &&
        currentTime - lastErrorTime < timeThreshold
      ) {
        consecutiveErrors++;
        if (consecutiveErrors >= errorThreshold) {
          toast.dismiss();

          toast.info(
            "You have a poor internet connection. Please check your internet connection.",
          );

          console.warn("Three consecutive non-200 responses within 5 seconds.");
        }
      } else {
        consecutiveErrors = 1;
      }
      lastErrorTime = currentTime;
      return Promise.reject(error);
    },
  );

  axios.interceptors.request.use(
    // @ts-ignore
    async (config: AxiosRequestConfig) => {
      const account = instance.getAllAccounts()[0];
      const { headers = {} } = config;
      let requestToken = "";

      headers.Authorization = `Bearer ${requestToken}`;

      if (!account) {
        return await instance.loginRedirect(redirectRequestConfig);
      }

      if (!requestToken.length) {
        try {
          const { accessToken } = await acquireMsalToken(instance, account);

          if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
            requestToken = accessToken;
          } else {
            await instance.logout();
          }
        } catch (error) {
          if (!(error instanceof InteractionRequiredAuthError)) {
            logger.critical(error as string);
          }

          if (!isAuthDisabled) {
            await instance.logout();
          }
        }
      }

      return { ...config, headers, requestToken };
    },
    error => {
      const fallbackErrorMessage = getReasonPhrase(
        StatusCodes.INTERNAL_SERVER_ERROR,
      );

      if (!error.response) {
        logger.critical(fallbackErrorMessage);
      } else {
        logger.critical(error.response.data ?? fallbackErrorMessage);
      }

      return Promise.reject(error);
    },
  );
};
